
// ADC-IMPLEMENTS: <adc-006a-feature-conncard-01>
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { type Connection } from "@/hooks/useConnections";
import { Trash2, Edit2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";

interface ConnectionCardProps {
  connection: Connection;
  onDelete: (id: string) => Promise<void>;
  onUpdate: (id: string, data: { name?: string; ageRating?: string }) => Promise<void>;
  onReconnect?: (id: string) => Promise<void>;
}

const editConnectionSchema = z.object({
  name: z.string().min(1, "Name is required"),
  ageRating: z.enum(["4+", "9+", "12+", "17+", "Restricted"])
});

export function ConnectionCard({ connection, onDelete, onUpdate, onReconnect }: ConnectionCardProps) {
  const [showQR, setShowQR] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    console.log("Connection data in card:", { 
      id: connection.id, 
      name: connection.name, 
      status: connection.status, 
      ageRating: connection.ageRating,
      flaggedMessageCount: connection.flaggedMessageCount 
    });
  }, [connection]);

  const form = useForm<z.infer<typeof editConnectionSchema>>({
    resolver: zodResolver(editConnectionSchema),
    defaultValues: {
      name: connection.name,
      ageRating: connection.ageRating as "4+" | "9+" | "12+" | "17+" | "Restricted" || "12+"
    }
  });

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(connection.id);
    } catch (error) {
      console.error("Error deleting connection:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdate = async (values: z.infer<typeof editConnectionSchema>) => {
    try {
      setIsUpdating(true);
      await onUpdate(connection.id, values);
      setShowEditDialog(false);
    } catch (error) {
      console.error("Error updating connection:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReconnect = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onReconnect) return;
    
    // Cerrar cualquier modal abierto del ConnectionCard antes del reconnect
    setShowQR(false);
    setShowEditDialog(false);
    
    try {
      setIsReconnecting(true);
      await onReconnect(connection.id);
      toast({
        title: "Reconnection initiated",
        description: "Attempting to reconnect to WhatsApp...",
      });
      setShowQR(false);
      
    } catch (error) {
      console.error("Error reconnecting:", error);
      toast({
        title: "Reconnection failed",
        description: error instanceof Error ? error.message : "Failed to reconnect",
        variant: "destructive",
      });
    } finally {
      setIsReconnecting(false);
    }
  };

  const handleFlaggedMessagesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (connection.flaggedMessageCount && connection.flaggedMessageCount > 0) {
      navigate(`/flagged-messages/${connection.id}`);
    }
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (showQR || showEditDialog) {
      return;
    }
    
    if (
      e.target instanceof Element && 
      (e.target.closest('button') || 
       e.target.closest('[role="button"]') || 
       e.target.closest('dialog') || 
       e.target.closest('[data-state="open"]'))
    ) {
      return;
    }
    
    navigate(`/flagged-messages/${connection.id}`);
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "connected") return "bg-green-500 hover:bg-green-600";
    if (statusLower === "disconnected") return "bg-red-500 hover:bg-red-600";
    if (statusLower === "connecting") return "bg-amber-500 hover:bg-amber-600";
    if (statusLower === "pairing" || statusLower === "pending" || statusLower === "wait_for_qrcode_auth") 
      return "bg-blue-500 hover:bg-blue-600";
    if (statusLower === "error") return "bg-destructive hover:bg-destructive/90";
    return "bg-gray-500 hover:bg-gray-600";
  };

  const getAgeRatingColor = (ageRating?: string) => {
    if (!ageRating) return "bg-gray-500";
    
    switch (ageRating) {
      case "4+": return "bg-emerald-500 hover:bg-emerald-600";
      case "9+": return "bg-blue-500 hover:bg-blue-600";
      case "12+": return "bg-amber-500 hover:bg-amber-600";
      case "17+": return "bg-orange-500 hover:bg-orange-600";
      case "Restricted": return "bg-red-500 hover:bg-red-600";
      default: return "bg-gray-500 hover:bg-gray-600";
    }
  };

  const needsQrCode = () => {
    if (connection.error) return false;
    
    const statusLower = connection.status?.toLowerCase() || '';
    return (statusLower === "pairing" || 
            statusLower === "pending" || 
            statusLower === "wait_for_qrcode_auth" ||
            statusLower === "refused") && 
            connection.qr;
  };

  const hasError = () => {
    return !!connection.error || connection.status?.toLowerCase() === "disconnected" || 
           connection.status?.toLowerCase() === "error" || connection.status?.toLowerCase() === "refused";
  };

  const getQrCodeSource = () => {
    if (!connection.qr) return '';
    
    if (connection.qr.startsWith('data:image')) {
      return connection.qr;
    }
    
    return `data:image/png;base64,${connection.qr}`;
  };

  return (
    <Card 
      className="glass animate-scale-in overflow-hidden group cursor-pointer" 
      onClick={handleCardClick}
    >
      <CardHeader className="p-4 pb-0">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge className={getStatusColor(connection.status || '')}>
                {connection.status || 'Unknown'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{connection.name}</h3>
              {connection.ageRating && (
                <Badge className={getAgeRatingColor(connection.ageRating)}>
                  {connection.ageRating}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity mr-1"
              onClick={(e) => {
                e.stopPropagation();
                setShowEditDialog(true);
              }}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                  disabled={isDeleting}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Connection</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete {connection.name}? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete();
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Created</span>
            <span>{format(connection.createdAt, 'MMM d, yyyy h:mm a')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Last Updated</span>
            <span>{format(connection.updatedAt, 'MMM d, yyyy h:mm a')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Flagged Messages</span>
            <div className="flex items-center">
              {connection.flaggedMessageCount && connection.flaggedMessageCount > 0 ? (
                <Badge
                  variant="destructive"
                  className="rounded-full cursor-pointer hover:bg-destructive/80"
                  onClick={handleFlaggedMessagesClick}
                >
                  {connection.flaggedMessageCount >= 99 ? '99+' : connection.flaggedMessageCount}
                </Badge>
              ) : (
                <span>0</span>
              )}
            </div>
          </div>
          {/* ADC-006A v2.0.0: Incident counts removed -- incidents browsed from IncidentDashboard */}
          {connection.error && (
            <div className="text-xs text-destructive mt-2">
              Error: {connection.error}
            </div>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="p-4 pt-0 flex justify-center">
        {needsQrCode() && (
          <Dialog open={showQR} onOpenChange={setShowQR}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                onClick={(e) => e.stopPropagation()}
              >
                Show QR Code
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Scan QR Code</DialogTitle>
                <DialogDescription>
                  Scan this QR code with your WhatsApp mobile app to connect.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-center p-4">
                <img 
                  src={getQrCodeSource()} 
                  alt="WhatsApp QR Code" 
                  className="max-w-full h-auto" 
                  onError={(e) => {
                    console.error("QR code image failed to load:", e);
                  }}
                  onLoad={() => {
                    console.log("QR code image loaded successfully in ConnectionCard");
                  }}
                />
              </div>
              <DialogFooter>
                <Button onClick={(e) => {
                  e.stopPropagation();
                  setShowQR(false);
                }}>Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        
        {hasError() && onReconnect && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleReconnect}
            disabled={isReconnecting}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isReconnecting ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Reconnecting...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reconnect
              </>
            )}
          </Button>
        )}
      </CardFooter>

      <Dialog open={showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setTimeout(() => setShowEditDialog(false), 0);
        }
      }}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Edit Connection</DialogTitle>
            <DialogDescription>
              Update the connection name or age rating.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={(e) => {
              e.stopPropagation();
              form.handleSubmit(handleUpdate)(e);
            }} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Connection Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter connection name" onClick={(e) => e.stopPropagation()} />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="ageRating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Age Rating</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value);
                      }}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger onClick={(e) => e.stopPropagation()}>
                          <SelectValue placeholder="Select age rating" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent onClick={(e) => e.stopPropagation()}>
                        <SelectItem value="4+">4+</SelectItem>
                        <SelectItem value="9+">9+</SelectItem>
                        <SelectItem value="12+">12+</SelectItem>
                        <SelectItem value="17+">17+</SelectItem>
                        <SelectItem value="Restricted">Restricted</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEditDialog(false);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={isUpdating}
                  onClick={(e) => e.stopPropagation()}
                >
                  {isUpdating ? "Updating..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
